import React, { useState, useEffect } from 'react';
import { auth, database, onAuthStateChanged, ref, get } from '../Auth/db/firebase';
import { Spin, notification, Button, Card } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import CashierMessaging from '../../components/shared/CashierMessaging';
import './index.css';

const CashierDashboard = () => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [api, contextHolder] = notification.useNotification();

  // Set page title
  useEffect(() => {
    document.title = `${facilityId} - User Dashboard | KioStock`;
  }, [facilityId]);

  const openNotification = (type, message) => {
    api[type]({
      message,
      placement: 'bottomRight',
      style: {
        background: '#14141465',
        color: '#fff',
        border: '1px solid #40a9ff',
        borderRadius: '8px',
        boxShadow: '0 3px 8px rgba(255, 255, 255, 0.2)',
      },
    });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // First, try to get user data from the specific facility
          let userSnapshot = await get(ref(database, `users/${facilityId}/${user.uid}`));
          
          if (userSnapshot.exists()) {
            setUserData({ ...userSnapshot.val(), facilityId });
          } else {
            // If not found in the specific facility, check if user is a superadmin in the 'me' collection
            const meSnapshot = await get(ref(database, `users/me`));
            if (meSnapshot.exists() && meSnapshot.val().email === user.email && meSnapshot.val().role === 'superadmin') {
              setUserData({ 
                ...meSnapshot.val(), 
                facilityId, 
                isSuperAdminVisiting: true 
              });
              openNotification('info', `Accessing ${facilityId} cashier page as Super Admin`);
            } else {
              // Check all facilities for superadmin
              const allUsersSnapshot = await get(ref(database, 'users'));
              let foundSuperAdmin = false;
              
              if (allUsersSnapshot.exists()) {
                const facilities = allUsersSnapshot.val();
                for (const fid in facilities) {
                  if (fid === 'me') continue;
                  const users = facilities[fid];
                  for (const uid in users) {
                    if (users[uid].email === user.email && users[uid].role === 'superadmin') {
                      setUserData({ 
                        ...users[uid], 
                        facilityId, 
                        originalFacilityId: fid,
                        isSuperAdminVisiting: true 
                      });
                      openNotification('info', `Accessing ${facilityId} cashier page as Super Admin`);
                      foundSuperAdmin = true;
                      break;
                    }
                  }
                  if (foundSuperAdmin) break;
                }
              }
              
              if (!foundSuperAdmin) {
                openNotification('error', 'User data not found or insufficient permissions.');
                setUserData(null);
              }
            }
          }
        } catch (err) {
          openNotification('error', `Error fetching user data: ${err.message}`);
          setUserData(null);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '50px auto' }} />;
  }

  if (!userData) {
    return <div className="cashier-container">No user data available.</div>;
  }

  return (
    <div className="cashier-container">
      {contextHolder}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, flex: 1 }}>Cashier Dashboard</h2>
        {userData.isSuperAdminVisiting && (
          <Button 
            type="primary" 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/superadmin')}
          >
            Back to Super Admin
          </Button>
        )}
      </div>
      {userData.isSuperAdminVisiting && (
        <div className="info-alert">
          <strong>Super Admin Access</strong> - You are viewing {facilityId} cashier dashboard as a Super Admin
          {userData.originalFacilityId && ` (originally from ${userData.originalFacilityId})`}
        </div>
      )}
      <p>Welcome, {userData.username}!</p>
      <p>Role: {userData.role}</p>
      <p>Facility ID: {facilityId}</p>
      
      <Card title="Communication" style={{ marginTop: '24px' }}>
        <CashierMessaging facilityId={facilityId} cashierData={userData} />
      </Card>
      
      <p style={{ marginTop: '24px' }}>This is the cashier dashboard for {facilityId}. Add your cashier features here.</p>
    </div>
  );
};

export default CashierDashboard;